import { useSelector } from 'react-redux';
import findResult from '@/utils/resultsHelper';
import { sortingFilters } from '@/constants/states';
import { findAudits } from '../state/state';
import allowAuditRead from '../helper';

const useAuditEntityFilter = ({ category, code }) => useSelector(({ audit: { audits } }) => {
  const { filters: { entity } } = findResult(audits, category, code) || { filters: { entity: '' } };
  return { entity };
});

const useAuditFilterRow = ({ category, code }) => useSelector(({ audit: { audits } }) => {
  const { filters } = findResult(audits, category, code);
  return filters;
});

const useAuditsActions = ({ category, code }) => {
  const { role, currentUser: { id } } = useSelector(({ auth }) => auth);
  return { allowRefresh: allowAuditRead(category, role, code === id) };
};

const auditsPanelState = ({
  results: audits, name, readErrors, readStatus,
}) => ({
  audits, name, readErrors, readStatus,
});

const auditsPanelPaging = ({ currentPage, totalPages: pageCount, pageSize }) => ({ currentPage, pageCount, pageSize });

const useAuditsPanelContainer = ({ category, code }) => useSelector(({
  audit: { audits },
  auth: { role, currentUser: { id } },
}) => {
  const { defaultAudit, item } = findAudits(audits, category, code);
  const canRead = item.isDefault ? false : allowAuditRead(category, role, code === id);
  const auditItem = canRead ? item : defaultAudit;

  return {
    exists: !auditItem.isDefault,
    canRead: auditItem.isDefault ? false : allowAuditRead(category, role, code === id),
    ...auditsPanelState(auditItem),
    pageSize: item.pageSize,
    total: auditItem.count,
  };
});

const useAuditsPanelNarrow = ({ category, code }) => useSelector(({ audit: { audits } }) => {
  const { item } = findAudits(audits, category, code);
  return {
    ...auditsPanelState(item),
    ...auditsPanelPaging(item),
  };
});

const allowLinks = ({
  cohorts, datasets, requests, roles, search, usergroups, users,
}) => ({
  allowCohortLink: cohorts.read,
  allowCollectionLink: datasets.read,
  allowDatasetLink: datasets.read,
  allowRequestLink: requests.read,
  allowRoleLink: roles.read,
  allowSearchLink: search.read,
  allowUserGroupLink: usergroups.read,
  allowUserLink: users.read,
});

const useAuditsTableRows = () => {
  const { role: { permissions } } = useSelector(({ auth }) => auth);
  return allowLinks(permissions);
};

const useAuditCardItems = () => {
  const { role: { permissions } } = useSelector(({ auth }) => auth);
  return allowLinks(permissions);
};

const useAuditsSortedColumn = ({ category }) => useSelector(({ audit: { audits } }) => {
  const results = audits[category]?.results || [];
  const { orderBy, singleSorting } = results[0] || sortingFilters();
  return { orderBy, singleSorting };
});

const useAuditsTableSettingsMenu = ({ category }) => useSelector(({ audit: { audits } }) => {
  const auditItems = audits[category];
  const results = auditItems ? auditItems.results : null;
  const item = results ? results[0] : null;
  return { singleSorting: item ? item.singleSorting : false };
});

const useAuditsTable = ({ category, code }) => useSelector(({ audit: { audits } }) => {
  const item = findResult(audits, category, code);

  return {
    ...auditsPanelState(item),
    ...auditsPanelPaging(item),
    total: item.count,
  };
});

export default {
  useAuditCardItems,
  useAuditEntityFilter,
  useAuditFilterRow,
  useAuditsActions,
  useAuditsPanelContainer,
  useAuditsPanelNarrow,
  useAuditsSortedColumn,
  useAuditsTable,
  useAuditsTableRows,
  useAuditsTableSettingsMenu,
};
